import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { translatorAPI } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { Users, Mail, CheckCircle, XCircle, Edit, Plus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const AdminTranslators = () => {
  const { user } = useOutletContext();
  const [translators, setTranslators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEmail, setEditingEmail] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({
    email: '',
    name: '',
    role: 'translator'
  });
  
  useEffect(() => {
    loadTranslators();
  }, []);
  
  const loadTranslators = async () => {
    try {
      const response = await translatorAPI.listTranslators();
      setTranslators(response.data);
    } catch (error) {
      console.error('Error loading translators:', error);
      toast.error('Failed to load translators');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateEmail = async (translatorId) => {
    if (!newEmail.trim()) {
      toast.error('Please enter a valid email');
      return;
    }
    
    try {
      await translatorAPI.updateCompanyEmail(translatorId, newEmail);
      toast.success('Company email updated successfully!');
      setEditingEmail(null);
      setNewEmail('');
      loadTranslators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    }
  };
  
  const handleAddTranslator = async (e) => {
    e.preventDefault();
    try {
      // Create user in database
      const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      await api.post('/users/create-manual', {
        user_id: userId,
        email: addForm.email,
        name: addForm.name,
        role: addForm.role
      });
      toast.success('Translator added successfully! They can now login with Google OAuth.');
      setShowAddDialog(false);
      setAddForm({ email: '', name: '', role: 'translator' });
      loadTranslators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add translator');
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading translators...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-primary mb-2">Manage Translators</h1>
          <p className="text-muted-foreground">View and manage translator profiles</p>
        </div>
        
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-accent" />
                All Translators ({translators.length})
              </CardTitle>
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-add-translator">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Translator
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Translator</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddTranslator} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="add_name">Full Name *</Label>
                      <Input
                        id="add_name"
                        value={addForm.name}
                        onChange={(e) => setAddForm({...addForm, name: e.target.value})}
                        required
                        data-testid="input-add-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="add_email">Email *</Label>
                      <Input
                        id="add_email"
                        type="email"
                        value={addForm.email}
                        onChange={(e) => setAddForm({...addForm, email: e.target.value})}
                        required
                        data-testid="input-add-email"
                      />
                    </div>
                    <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">
                        The translator will be able to login using Google OAuth with this email.
                        They must complete their profile and sign the NDA after first login.
                      </p>
                    </div>
                    <Button type="submit" className="w-full" data-testid="btn-save-translator">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Translator
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {translators.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No translators found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {translators.map((translator) => (
                  <Card 
                    key={translator.user_id}
                    className="hover:shadow-md transition-shadow"
                    data-testid={`translator-${translator.user_id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent to-creative flex items-center justify-center text-white font-bold text-lg">
                              {translator.full_name?.[0]?.toUpperCase() || 'T'}
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-primary">{translator.full_name}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge 
                                  className={translator.nda_signed 
                                    ? 'bg-success/10 text-success' 
                                    : 'bg-warning/10 text-warning'
                                  }
                                >
                                  {translator.nda_signed ? (
                                    <><CheckCircle className="w-3 h-3 mr-1" /> NDA Signed</>
                                  ) : (
                                    <><XCircle className="w-3 h-3 mr-1" /> NDA Pending</>
                                  )}
                                </Badge>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {translator.status}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-muted-foreground mb-1">Personal Email</p>
                              <p className="font-medium text-primary">{translator.personal_email}</p>
                            </div>
                            
                            <div>
                              <p className="text-muted-foreground mb-1">Company Email</p>
                              {editingEmail === translator.user_id ? (
                                <div className="flex gap-2">
                                  <Input
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    placeholder="company@graphikpro.com"
                                    className="h-8"
                                    data-testid="input-company-email"
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateEmail(translator.user_id)}
                                    data-testid="btn-save-email"
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingEmail(null);
                                      setNewEmail('');
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-primary">
                                    {translator.company_email || 'Not assigned'}
                                  </p>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingEmail(translator.user_id);
                                      setNewEmail(translator.company_email || '');
                                    }}
                                    data-testid={`btn-edit-email-${translator.user_id}`}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                            
                            <div>
                              <p className="text-muted-foreground mb-1">Phone</p>
                              <p className="font-medium text-primary">{translator.phone || 'N/A'}</p>
                            </div>
                            
                            <div>
                              <p className="text-muted-foreground mb-1">PAN</p>
                              <p className="font-medium text-primary font-mono">{translator.pan || 'N/A'}</p>
                            </div>
                            
                            {translator.gstin && (
                              <div>
                                <p className="text-muted-foreground mb-1">GSTIN</p>
                                <p className="font-medium text-primary font-mono">{translator.gstin}</p>
                              </div>
                            )}
                            
                            <div>
                              <p className="text-muted-foreground mb-1">Joined</p>
                              <p className="font-medium text-primary">{formatDate(translator.created_at)}</p>
                            </div>
                          </div>
                          
                          {translator.nda_signed_at && (
                            <p className="text-xs text-muted-foreground mt-3">
                              NDA signed on {formatDate(translator.nda_signed_at)} (v{translator.nda_version})
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminTranslators;
